/**
 * Canonical content ids — OWNER: content builder, but treat as a SHARED CONTRACT:
 * bots, web and harness reference these constants. Add ids freely; never rename.
 */
export const CLASS = {
  warden: 'warden',
  lightbinder: 'lightbinder',
  blademaster: 'blademaster',
  stormcaller: 'stormcaller',
} as const;

/**
 * +classes: the five playable base classes (docs/design/classes/advancement.md). The four ids in
 * CLASS above are the retired M+ kits (kept for the legacy harness only).
 */
export const BASE_CLASS = {
  warrior: 'warrior',
  rogue: 'rogue',
  mage: 'mage',
  acolyte: 'acolyte',
  ranger: 'ranger',
} as const;

/** +classes: base-class ability ids, in unlock order (levels 1–7 = keys 1–7; docs/design/classes/advancement.md). */
export const BASE_ABILITY = {
  warrior: {
    breaker: 'warrior.breaker', // 1
    headlong: 'warrior.headlong', // 2
    wideArc: 'warrior.wide_arc', // 3
    rive: 'warrior.rive', // 4
    bloodprize: 'warrior.bloodprize', // 5
    shieldCheck: 'warrior.shield_check', // 6
    ironBreath: 'warrior.iron_breath', // 7
  },
  rogue: {
    nick: 'rogue.nick', // 1
    finale: 'rogue.finale', // 2
    lurk: 'rogue.lurk', // 3
    dirtyOpener: 'rogue.dirty_opener', // key 1 while Lurking
    slip: 'rogue.slip', // 4
    bladefan: 'rogue.bladefan', // 5
    ashveil: 'rogue.ashveil', // 6
    throatjab: 'rogue.throatjab', // 7
  },
  mage: {
    manaDart: 'mage.mana_dart', // 1
    arcaneLance: 'mage.arcane_lance', // 2
    hoarbind: 'mage.hoarbind', // 3
    foldStep: 'mage.fold_step', // 4
    flareBurst: 'mage.flare_burst', // 5
    glyphShield: 'mage.glyph_shield', // 6
    wanderingStar: 'mage.wandering_star', // 7
  },
  acolyte: {
    lightlash: 'acolyte.lightlash', // 1
    dawnbrand: 'acolyte.dawnbrand', // 2
    tend: 'acolyte.tend', // 3
    aegis: 'acolyte.aegis', // 4
    sanctumCircle: 'acolyte.sanctum_circle', // 5
    hearthward: 'acolyte.hearthward', // 6
    dazzle: 'acolyte.dazzle', // 7
  },
  ranger: {
    trueDraw: 'ranger.true_draw', // 1
    longshot: 'ranger.longshot', // 2
    snareCoil: 'ranger.snare_coil', // 3
    backflip: 'ranger.backflip', // 4
    arrowfall: 'ranger.arrowfall', // 5
    swiftfoot: 'ranger.swiftfoot', // 6
    pinShot: 'ranger.pin_shot', // 7
  },
} as const;

/** +classes: base-class aura ids. */
export const BASE_AURA = {
  warriorThreat: 'warrior.threat_aura',
  killSense: 'warrior.kill_sense', // hidden passive: kill credit opens Fresh Kill
  freshKill: 'warrior.fresh_kill', // Bloodprize's 20 s window
  headlongRoot: 'warrior.headlong_root',
  rive: 'warrior.rive_bleed',
  ironBreath: 'warrior.iron_breath_buff',
  lurk: 'rogue.lurk_buff',
  reeling: 'rogue.reeling_stun',
  ashveil: 'rogue.ashveil_buff',
  hoarbound: 'mage.hoarbound_root',
  glyphShield: 'mage.glyph_shield_absorb',
  dawnbrand: 'acolyte.dawnbrand_dot',
  aegis: 'acolyte.aegis_absorb',
  hearthward: 'acolyte.hearthward_buff',
  dazzled: 'acolyte.dazzled_stun',
  snareCoil: 'ranger.snare_coil_root',
  swiftfoot: 'ranger.swiftfoot_buff',
} as const;

/** Old class id per base class (the four retired kits; Ranger has none). Profile migration v5 → v6. */
export const RETIRED_CLASS_TO_BASE: Readonly<Record<string, string>> = { warden: 'warrior', lightbinder: 'acolyte', blademaster: 'rogue', stormcaller: 'mage' };

/** Ability ids per class, keyed by PLAN §4 bind. */
export const ABILITY = {
  /** Shared by every hero (Space). */
  common: {
    dodge: 'common.dodge',
  },
  warden: {
    shieldSlam: 'warden.shield_slam', // 1
    cleaveStrike: 'warden.cleave_strike', // 2
    bulwarkStrike: 'warden.bulwark_strike', // 3
    consecratedGround: 'warden.consecrated_ground', // 4
    lastStand: 'warden.last_stand', // Q
    shieldWallBash: 'warden.shield_wall_bash', // E (off-GCD)
    shieldBash: 'warden.shield_bash', // R interrupt
    taunt: 'warden.taunt', // F
    grip: 'warden.grip', // G (extraBinds)
    // Warden rotation rework (docs/design/classes/warden.md).
    aegisThrow: 'warden.aegis_throw', // 5
    riposte: 'warden.riposte', // 6
    aegisRecall: 'warden.aegis_recall', // key 5 transform (off-GCD) while Aegis Recall is up
  },
  lightbinder: {
    flashHeal: 'lightbinder.flash_heal', // 1
    holyShock: 'lightbinder.holy_shock', // 2
    wordOfLight: 'lightbinder.word_of_light', // 3
    judgment: 'lightbinder.judgment', // 4
    radiantWave: 'lightbinder.radiant_wave', // Q
    guardiansAegis: 'lightbinder.guardians_aegis', // E
    cleanse: 'lightbinder.cleanse', // R dispel
    divineShield: 'lightbinder.divine_shield', // F
    blessingOfFreedom: 'lightbinder.blessing_of_freedom', // G
    dawnsong: 'lightbinder.dawnsong', // 5 (rotation rework)
    dawnbringer: 'lightbinder.dawnbringer', // 6 (rotation rework)
    sunfall: 'lightbinder.sunfall', // 6 transform (Dawnbringer → Sunfall while Sunfall is offered)
  },
  blademaster: {
    mortalStrike: 'blademaster.mortal_strike', // 1
    overpower: 'blademaster.overpower', // 2
    cleave: 'blademaster.cleave', // 3
    execute: 'blademaster.execute', // 4
    bladestorm: 'blademaster.bladestorm', // Q
    colossusSmash: 'blademaster.colossus_smash', // E
    pummel: 'blademaster.pummel', // R interrupt
    dieByTheSword: 'blademaster.die_by_the_sword', // F
    charge: 'blademaster.charge', // extra
    slam: 'blademaster.slam', // 5 (rotation rework)
    rend: 'blademaster.rend', // 6 (rotation rework)
    ravagersFinale: 'blademaster.ravagers_finale', // Q while Ravager's Finale is up (transform form)
  },
  stormcaller: {
    lightningBolt: 'stormcaller.lightning_bolt', // 1
    lavaBurst: 'stormcaller.lava_burst', // 2
    chainLightning: 'stormcaller.chain_lightning', // 3
    earthquake: 'stormcaller.earthquake', // 4
    stormkeeper: 'stormcaller.stormkeeper', // Q
    flameShock: 'stormcaller.flame_shock', // E
    windShear: 'stormcaller.wind_shear', // R interrupt
    astralShift: 'stormcaller.astral_shift', // F
    // Stormcaller rotation rework (docs/design/classes/stormcaller.md).
    earthShock: 'stormcaller.earth_shock', // 5
    arcTorrent: 'stormcaller.arc_torrent', // 6 (short burst CD, channel)
    tempest: 'stormcaller.tempest', // key 1 transform while Tempest is up
  },
} as const;

/** Aura ids bots/HUD care about. */
export const AURA = {
  wardenThreat: 'warden.threat_aura',
  consecratedGround: 'warden.consecrated_ground_dr',
  lastStand: 'warden.last_stand',
  shieldWallBash: 'warden.shield_wall_bash',
  // Warden rotation rework: procs, the transform and the Bastion stack.
  opening: 'warden.opening',
  shieldMomentum: 'warden.shield_momentum',
  aegisRebound: 'warden.aegis_rebound',
  aegisRecallReady: 'warden.aegis_recall_ready',
  bastion: 'warden.bastion',
  radiantWaveHot: 'lightbinder.radiant_wave_hot',
  guardiansAegis: 'lightbinder.guardians_aegis',
  divineShield: 'lightbinder.divine_shield',
  // Lightbinder rotation rework: procs, windows and stacks.
  infusionOfLight: 'lightbinder.infusion_of_light',
  gloriousDawn: 'lightbinder.glorious_dawn',
  divinePurpose: 'lightbinder.divine_purpose',
  avengingLight: 'lightbinder.avenging_light',
  sunfallReady: 'lightbinder.sunfall_ready',
  dawnlight: 'lightbinder.dawnlight',
  cleaveBuff: 'blademaster.cleave_buff',
  overpowerProc: 'blademaster.overpower_proc',
  colossusSmash: 'blademaster.colossus_smash',
  dieByTheSword: 'blademaster.die_by_the_sword',
  bladestorm: 'blademaster.bladestorm',
  suddenDeath: 'blademaster.sudden_death', // proc: free Execute at any HP (2 stacks)
  wounds: 'blademaster.wounds', // debuff stack (max 5) cashed by MS / Execute / Finale
  ravagersFinaleReady: 'blademaster.ravagers_finale_ready', // Q → Ravager's Finale
  shockwaveReady: 'blademaster.shockwave_ready', // talent: 5 → Shockwave (blademaster.shockwave, added by the talent)
  rend: 'blademaster.rend', // bleed (same id as the ability, like Flame Shock)
  flameShock: 'stormcaller.flame_shock',
  stormkeeper: 'stormcaller.stormkeeper',
  astralShift: 'stormcaller.astral_shift',
  // Stormcaller rotation rework: procs, the transform and the Static Charge stack.
  lavaSurge: 'stormcaller.lava_surge', // proc: +1 Lava Burst charge, next Lava Burst instant
  masterOfElements: 'stormcaller.master_of_elements', // buff: next Earth Shock / Tempest / Chain Lightning +20%
  staticCharge: 'stormcaller.static_charge', // stack (max 5) cashed by Earth Shock / Earthquake
  gatheringStorm: 'stormcaller.gathering_storm', // spender counter: every 3rd spender grants Tempest
  tempest: 'stormcaller.tempest_ready', // key 1 → Tempest
} as const;

export const DUNGEON = {
  venomAltar: 'venom_altar',
  /** Tiny one-room arena with dummies/one pack for tests (M0/M1). */
  trainingGrounds: 'training_grounds',
} as const;

export const ENCOUNTER = {
  ravager: 'ravager',
  coil: 'the_coil',
  chieftain: 'chieftain',
} as const;

// +content: extra class ids (passives, procs) and all enemy content ids.

/** Class passives and internal auras. */
export const PASSIVE = {
  wardenThreat: 'warden.threat_aura',
  wardenRhythm: 'warden.rhythm', // hidden: every Warden proc roll and per-hit Bastion grant (rotation rework)
  bladeTactician: 'blademaster.tactician',
  bladeSuddenDeath: 'blademaster.sudden_death_passive',
  lightEngine: 'lightbinder.light_engine', // hidden: every Lightbinder proc roll (rotation rework)
  stormLavaSurge: 'stormcaller.lava_surge_passive', // hidden: Flame Shock ticks roll Lava Surge (rotation rework)
  stormClOverflow: 'stormcaller.cl_overflow', // hidden: Chain Lightning +4 Maelstrom and +1 Static Charge per enemy hit
} as const;

/** Every NPC id. Trash archetypes follow PLAN §1/§5. */
export const NPC = {
  // trash (8 archetypes)
  venomspawn: 'venomspawn', // fodder, 1 forces
  fangReaver: 'fang_reaver', // bruiser w/ tank-buster, 5 forces
  cultAdept: 'cult_adept', // bolt caster, 7 forces
  spittingSerpent: 'spitting_serpent', // frontal spitter, 7 forces
  coilMesmer: 'coil_mesmer', // CC caster, 7 forces
  venomPriestess: 'venom_priestess', // healer/shielder, 7 forces
  hoodedConstrictor: 'hooded_constrictor', // big, 25 forces
  elderBasilisk: 'elder_basilisk', // mini-boss, 30 forces
  // bosses + adds
  ravager: 'ravager',
  ravenousWhelp: 'ravenous_whelp',
  theCoil: 'the_coil',
  uncoiledWrithe: 'uncoiled_writhe',
  chieftain: 'chieftain',
  sacrificialCultist: 'sacrificial_cultist', // +encounters: Blood Offering intermission add
  // test target
  trainingDummy: 'training_dummy',
} as const;

/** Every enemy ability id. */
export const ENEMY = {
  rendFlesh: 'npc.reaver.rend_flesh',
  venomBolt: 'npc.adept.venom_bolt',
  acidSpew: 'npc.serpent.acid_spew',
  hypnoticGaze: 'npc.mesmer.hypnotic_gaze',
  serpentsMending: 'npc.priestess.serpents_mending',
  scaleWard: 'npc.priestess.scale_ward',
  crushingCoils: 'npc.constrictor.crushing_coils',
  venomNova: 'npc.constrictor.venom_nova',
  tailLash: 'npc.basilisk.tail_lash',
  petrifyingGlare: 'npc.basilisk.petrifying_glare',
  venomRain: 'npc.basilisk.venom_rain',
  // Ravager
  rendingMaw: 'ravager.rending_maw',
  tripleShot: 'ravager.triple_shot',
  ravenousStomp: 'ravager.ravenous_stomp',
  regurgitate: 'ravager.regurgitate',
  // The Coil
  toxicAtrophy: 'coil.toxic_atrophy',
  burrowingCharge: 'coil.burrowing_charge',
  venomJet: 'coil.venom_jet',
  tailScythe: 'coil.tail_scythe',
  assimilate: 'coil.writhe_assimilate',
  writheToxicAtrophy: 'coil.writhe_toxic_atrophy',
  // Chieftain
  ritualOfTheFang: 'chieftain.ritual_of_the_fang',
  boneCyclone: 'chieftain.bone_cyclone',
  chopDown: 'chieftain.chop_down',
  boneslicer: 'chieftain.boneslicer',
  // +encounters: mechanics rework (docs/design/encounters.md)
  bloodFrenzy: 'npc.reaver.blood_frenzy',
  ascensionRite: 'npc.adept.ascension_rite',
  piercingHiss: 'npc.serpent.piercing_hiss',
  feedingFrenzy: 'ravager.feeding_frenzy',
  huntingPounce: 'ravager.hunting_pounce',
  synchronizedVenom: 'coil.synchronized_venom',
  toxicAtrophyP2: 'coil.toxic_atrophy_p2',
  deathRattle: 'coil.death_rattle',
  venomTorrent: 'coil.venom_torrent',
  coilingVortex: 'coil.coiling_vortex',
  recoil: 'coil.writhe_recoil',
  ritualOfTheFangP2: 'chieftain.ritual_of_the_fang_p2',
  axegrinder: 'chieftain.axegrinder',
  bloodroar: 'chieftain.bloodroar',
  bloodrage: 'chieftain.bloodrage',
  bloodSacrifice: 'chieftain.blood_sacrifice',
  ascensionRiteEarly: 'npc.adept.ascension_rite_7', // +7: an extra Rite 4 s into the pull
} as const;

/** Enemy auras bots/HUD care about (dispel targets, CC). */
export const ENEMY_AURA = {
  lacerate: 'npc.reaver.lacerate',
  acidBurn: 'npc.serpent.acid_burn', // poison
  hypnosis: 'npc.mesmer.hypnosis', // magic, disorient
  scaleWard: 'npc.priestess.scale_ward', // absorb on NPC
  venomNova: 'npc.constrictor.venom_nova', // poison
  petrified: 'npc.basilisk.petrified', // magic, stun
  toxicAtrophy: 'coil.toxic_atrophy', // poison
  fangRitual: 'chieftain.fang_ritual',
  boneslice: 'chieftain.boneslice',
  // +encounters: mechanics rework
  septicSpatter: 'npc.venomspawn.septic', // −healing taken from Venomspawn puddles (+7)
  bloodFrenzy: 'npc.reaver.blood_frenzy', // enrage
  bloodFrenzyEmpowered: 'npc.reaver.blood_frenzy_7',
  hemorrhage: 'npc.reaver.hemorrhage',
  envenom: 'npc.adept.envenom', // poison, stacks
  ascended: 'npc.adept.ascended',
  piercingHiss: 'npc.serpent.silenced', // silence
  scaleWardHaste: 'npc.priestess.scale_ward_haste',
  coagulatingVenom: 'npc.constrictor.coagulating_venom', // heal absorb
  constricted: 'npc.constrictor.constricted',
  petrifiedBrittle: 'npc.basilisk.petrified_10', // magic, stun, +50% damage taken
  septicBitePassive: 'ravager.septic_bite_passive',
  septicBite: 'ravager.septic_bite', // tank stacks, consumed by Rending Maw
  barbed: 'ravager.barbed',
  bileBurn: 'ravager.bile_burn',
  whelpFixate: 'ravager.whelp_fixate',
  gorgedHide: 'ravager.gorged_hide', // shield; Feeding Frenzy is kickable once it breaks
  feedingFrenzy: 'ravager.feeding_frenzy',
  gorged: 'ravager.gorged',
  syncVenom: 'coil.synchronized_venom',
  syncVenomEmpowered: 'coil.synchronized_venom_7',
  sluggishVenom: 'coil.sluggish_venom',
  burrowed: 'coil.burrowed',
  writheFixate: 'coil.writhe_fixate',
  venomJetPoison: 'coil.venom_jet_poison',
  ritualVenom: 'chieftain.ritual_venom', // explodes on expiry; Bone Shards clear it
  fangEmpowerment: 'chieftain.fang_empowerment',
  chopDown: 'chieftain.chop_down_combo',
  bloodOffering: 'chieftain.blood_offering', // boss immunity during the intermission
  bloodSacrifice: 'chieftain.blood_sacrifice', // heal absorb
  bloodroarBleed: 'chieftain.bloodroar_bleed',
  bloodrage: 'chieftain.bloodrage',
  bloodrageEmpowered: 'chieftain.bloodrage_10',
  ravenousPassive: 'ravager.whelp_ravenous_passive', // +10: whelp swings stack Ravenous
  ravenous: 'ravager.whelp_ravenous', // enrage, +10% damage per stack
} as const;
